// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SnippetFolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import type { AlertColor } from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { styled, useTheme, type Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { traverserStopRowCellStyles } from "_shared/_views/highlights/traverser-stop-highlight";
import { traverserStopClassName } from "_shared/_views/highlights/traverser-stop-scope";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TableAddActions, { type TableAddAction } from "_shared/_views/tables/table-add-actions";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
} from "_shared/_views/tables/table-toolbar";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import { feeScheduleCategoryTypeLabel } from "_shared/fee-schedules/categories/types/fee-schedule-category-type";
import {
  FeeScheduleTableCellNoValue,
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
  removedTableRowCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_SortingState as SortingState,
  type MRT_Updater,
} from "material-react-table";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import {
  isFeeScheduleTemplateLoadIssue
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import FeeScheduleIssueFlag, {
  feeScheduleOffTeamFlagText,
  type FeeScheduleIssueFlagSeverity,
} from "_shared/fee-schedules/categories/_views/fee-schedule-issue-flag";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import {
  proposalCollectionItemRevertAspect,
  proposalCollectionItemRevertValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import { proposalFeeScheduleAspectOptions } from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import {
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberFallbackText,
  proposalFeeScheduleUnassignedText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import { useRemovedProposalFeeScheduleCategories } from "work/proposals/negotiation/proposal-fee-schedule-removed-categories";
import {
  fieldHasPendingChange,
  pendingChangeTableRowStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { ProposalIssueLevel, ProposalIssuePart, type ProposalIssue } from "work/proposals/issues/proposal-issues";
import { feeScheduleIssueCellKey } from "work/proposals/issues/fee-schedule-issue-cell";
import { useWorkPaneNarrowerThan } from "work/_views/use-work-pane-size";
import RowLink from "_shared/_views/tables/row-link";
import { feeScheduleColumnWidth } from "work/fee-schedule-templates/_views/tables/fee-schedule-table-columns";
import FieldUndoButton, { FIELD_UNDO_LABEL } from "work/proposals/_views/_buttons/field-undo-button";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import FeeScheduleCategory from "../../../../_shared/fee-schedules/categories/fee-schedule-category";
import { paneStretchTable } from "_shared/_views/tables/table-pane-stretch";
import { usePersistedColumnVisibility } from "_shared/_views/tables/table-column-visibility-preference";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
// #endregion

// #region Styled Components
/**
 * The row the traverser is on, and any pending change on it, as one set of row styles.
 *
 * The band is drawn inside the cells — see `traverserStopRowCellStyles` — and not as a ring around
 * the `<tr>`. A ring around the row only shows in a table whose borders are separate if the rows are
 * held apart, so this table used to open 3px of `border-spacing` while a row pulsed: seven gaps across
 * six columns added 21px to a `table-layout: fixed` table whose column widths had already been spent
 * to the pixel, and it grew a horizontal scrollbar with nothing to scroll to. On the `<tr>`
 * itself the ring is covered, since a cell paints above its row.
 *
 * The two are merged cell by cell. They used to be spread one after the other, so the stop's cell
 * styles replaced the pending change's outright and a pending row lost its tint while it was the stop.
 * Where both draw a `box-shadow`, the stop's pulse wins: the traverser having just landed on the row
 * is the more urgent of the two, and the tint and underline colour still say it is pending.
 */
function feeScheduleCategoryRowStyles(theme: Theme, hasPendingChange: boolean, isActive: boolean) {
  const pendingCells = hasPendingChange ? pendingChangeTableRowStyles(theme)["& > td"] : {};
  if (!isActive) return { "& > td": { ...pendingCells, animation: "none" } };
  return {
    ...traverserStopRowCellStyles,
    "& > td": { ...pendingCells, ...traverserStopRowCellStyles["& > td"] },
  };
}
/**
 * A column's width before the table is measured: the first paint, and a renderer with no layout. Once
 * drawn, the shared list-table fit sizes the column from its heading and content (#6708).
 */
function startingWidth(columnId: string): { size: number; minSize: number } {
  const width = feeScheduleColumnWidth(columnId);
  return { size: width.preferred, minSize: width.minimum };
}
const TableContainer = styled('div')(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
/** The row's Edit action, at the density of the Remove Category button beside it. */
const EditActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const LockedCellContent = styled("span")({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: 0,
  verticalAlign: "middle",
  width: "100%",
});
// #endregion

/** What this schedule's add control calls its three ways of gaining a category. */
export const ADD_NEW_CATEGORY_LABEL = "Add New Category";
export const ADD_EXISTING_CATEGORY_LABEL = "Add Existing Category";
export const FROM_EXISTING_TEMPLATE_LABEL = "From Existing Template";

const columnHelper = createMRTColumnHelper<
  FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
>();

// #region Props
type FeeScheduleTemplatesTableProps = {
  className?: string;
  selectedCategories:
  | Array<FeeScheduleCategory>
  | Array<FeeScheduleCategoryMaskedCurrency>;
  disableEditing?: boolean;
  disableCategorySelection?: boolean;
  hideFromTemplateButton?: boolean;
  hideAddButton?: boolean;
  hideExistingButton?: boolean;
  /**
   * Hides each category row's change-history control, and with it the rows the history alone can
   * supply: the categories the schedule no longer has, drawn struck through (#4951).
   */
  hideFieldHistory?: boolean;
  hideActionsColumn?: boolean;
  hideTeamMemberColumn?: boolean;
  /**
   * The proposal's vendor team. Given, a rate naming somebody it does not have is flagged in the
   * Team Member column (#5798). A template has no team and passes none.
   */
  teamRoster?: FeeScheduleTeamRoster;
  /**
   * The Team Member issue on each category that has one, by category id. Given, that category's Team
   * Member cell is coloured at the issue's level, with the issue's words in its tooltip (#6097).
   */
  teamMemberIssues?: ReadonlyMap<string, ProposalIssue>;
  /**
   * The Fee issue on each category that has one, a fee deferred to the other side and not set yet,
   * by category id. Given, that category's Fee cell is coloured as a Team Member cell is (#6097).
   */
  feeIssues?: ReadonlyMap<string, ProposalIssue>;
  negotiationId?: string;
  viewerScope?: string;
  alertBannerMessage?: string;
  alertBannerSeverity?: AlertColor;
  activeReviewField?: ProposalField;
  /**
   * Hears the categories in the order the table draws them, sorted and filtered as the reader left it,
   * so a traverser walks the rows in that order (#6617). Undefined once the table is gone.
   */
  onDisplayedCategoriesChange?: (categoryIds: readonly Guid[] | undefined) => void;
  fieldLockTooltip?: string;
  pendingChangeFields?: ProposalField[];
  /**
   * Extra actions rendered alongside the built-in add-category buttons in the top toolbar, so a
   * caller's own action (e.g. the proposal fee schedule tab's "Create Template") lines up with them
   * instead of sitting in a separate row above the table.
   */
  toolbarActions?: ReactNode;
  /**
   * An editor to draw underneath one row, instead of over the page in a dialog (issue #5111).
   *
   * Opt-in, and off wherever it is not passed: a caller that still opens a dialog from
   * `onEditCategory` gets the table exactly as it was. The caller says which row is open through
   * `editingCategoryId` and closes it from its own editor, because the row is not the only thing
   * that can end the edit — saving, cancelling and removing the category all do.
   *
   * The expander column MRT would add for this is deliberately hidden: the row's Edit action, its
   * name link and a press on the row already open the editor, and an expander would be a fourth
   * control for the same thing. It would also have no width under this table's fixed layout.
   */
  renderCategoryEditor?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => ReactNode;
  /** The id of the category whose inline editor is open. @see renderCategoryEditor */
  editingCategoryId?: string;
  onCreateCategoryClicked?: () => void;
  onExistingCategoryClicked?: () => void;
  onExistingTemplateClicked?: () => void;
  /**
   * Save as Template, from the proposal tab that holds this schedule. Its own button beside the add
   * control, not an entry in its dropdown, so a reader sees it without opening a menu (#5808).
   */
  saveAsTemplateAction?: TableAddAction;
  onEditCategory?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryRemoved?: (name: FeeScheduleCategoryName) => void;
  /**
   * Called with the category reverted to a prior value (parsed from that value's history entry).
   * The caller applies it the same way it applies any other category edit (replacing the entry by id
   * in the selected-categories array and re-saving via its own builder setter), so the revert flows
   * through the same `queueProposalUpdate` path as a manual edit and lands as a
   * `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`.
   */
  onCategoryReverted?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  /**
   * A saved draft's changes to this schedule since its last save, and their Undo (#6995). Absent
   * everywhere else.
   */
  draftChanges?: FeeScheduleDraftChanges;
};

/**
 * What a saved draft's schedule differs from its saved self by.
 *
 * A category the draft removed is not in `selectedCategories`, so it is given here. It stays drawn,
 * struck through, with Undo in place of Remove until the save. An edited category keeps its row and
 * offers Undo beside Remove. An added one keeps Remove alone: on a draft, Remove and Undo do the same
 * thing to it.
 */
export type FeeScheduleDraftChanges = {
  removedCategories: ReadonlyArray<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>;
  editedCategoryIds: ReadonlySet<string>;
  /**
   * Why an edited category's Undo cannot be done yet, by category id: another category holds the name
   * it would put back. Its Undo is drawn disabled, saying so.
   */
  undoBlockedReasons?: ReadonlyMap<string, string>;
  onCategoryUndone: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
};

/** No draft removals: one array, so the rows below are not rebuilt on every render. */
const NO_DRAFT_REMOVED_CATEGORIES: ReadonlyArray<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> = [];
// #endregion

export default function FeeScheduleTemplatesTable(
  props: Readonly<FeeScheduleTemplatesTableProps>,
) {
  const {
    className,
    selectedCategories,
    disableEditing,
    disableCategorySelection,
    hideFromTemplateButton,
    hideAddButton,
    hideExistingButton,
    hideFieldHistory,
    hideActionsColumn,
    hideTeamMemberColumn,
    teamRoster,
    teamMemberIssues,
    feeIssues,
    negotiationId,
    viewerScope,
    alertBannerMessage,
    activeReviewField,
    onDisplayedCategoriesChange,
    fieldLockTooltip,
    pendingChangeFields,
    toolbarActions,
    renderCategoryEditor,
    editingCategoryId,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onExistingTemplateClicked,
    saveAsTemplateAction,
    onEditCategory,
    onCategoryRemoved,
    onCategoryReverted,
    draftChanges,
  } = props;
  const alertBannerSeverity = props.alertBannerSeverity ?? "warning";

  // #region State
  const [query, setQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });

  // #region Hooks
  const theme = useTheme();
  // On a phone the row's Edit and Remove Category fold into one menu, as the active proposal's fee
  // schedule does. The viewport, not the pane: see "The exception" in table-row-actions.md (#5907).
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const session = useSession();
  const confirm = useConfirmDialog();
  /**
   * How much room this table has, which on a proposal page is not how much room the window has: the
   * page's comments and category panels take a column out of the row the table sits in, so a 1280px
   * display with comments open left the table 608px while `down("lg")` still called it a large one and
   * kept the desktop toolbar (#4964). Off a proposal page there is no pane to measure and both of
   * these answer from the viewport, exactly as they did.
   */
  const tableIsCompact = useWorkPaneNarrowerThan(theme.breakpoints.values.md);
  /** Columns the reader hid or restored themselves, from the Show/Hide columns menu. */
  const [chosenColumnVisibility, setChosenColumnVisibility] = usePersistedColumnVisibility("fee-schedule-categories");
  // #region Removed categories
  // The categories the schedule no longer has, read from its own history and kept as struck-through,
  // read-only rows so each keeps the history of the category it was (#4951) — the whole-schedule history
  // control that used to be the only place they survived is gone. They are not in `selectedCategories`,
  // so nothing that edits the schedule (a row click, Remove, a revert) is offered on them.
  //
  // A saved draft's own removals since its last save are drawn the same way, from `draftChanges`, and
  // offer Undo (#6995). Each is still in the saved schedule, so the history has no removal for it; it
  // counts as present below, so that it cannot come back from the history as a second row as well.
  const showFieldHistory = Boolean(!hideFieldHistory && negotiationId && viewerScope);
  const draftRemovedCategories = draftChanges?.removedCategories ?? NO_DRAFT_REMOVED_CATEGORIES;
  const presentCategoryIds = useMemo(
    () => [...selectedCategories, ...draftRemovedCategories].flatMap((category) => (category.id ? [category.id.value] : [])),
    [draftRemovedCategories, selectedCategories],
  );
  const removedCategories = useRemovedProposalFeeScheduleCategories({
    negotiationId: showFieldHistory ? negotiationId : undefined,
    viewerScope: showFieldHistory ? viewerScope : undefined,
    presentItemIds: presentCategoryIds,
  });
  const draftRemovedCategoryIds = useMemo(
    () => new Set(draftRemovedCategories.map((category) => category.id.value)),
    [draftRemovedCategories],
  );
  const removedCategoryIds = useMemo(
    () => new Set([...draftRemovedCategoryIds, ...removedCategories.map((removed) => removed.category.id.value)]),
    [draftRemovedCategoryIds, removedCategories],
  );
  const isRemovedCategory = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null | undefined) =>
      Boolean(category?.id && removedCategoryIds.has(category.id.value)),
    [removedCategoryIds],
  );
  const rows = useMemo(
    () => [...selectedCategories, ...draftRemovedCategories, ...removedCategories.map((removed) => removed.category)],
    [draftRemovedCategories, removedCategories, selectedCategories],
  );
  // #endregion

  const teamMemberUserIds = useMemo(
    () => new Set(rows.flatMap((category) => category.teamMember?.userId.value ?? [])),
    [rows],
  );
  const teamMemberIndividualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberUserIds.size > 0 ? { userIds: [...teamMemberUserIds] } : skipToken,
  );
  const teamMemberNames = useMemo(
    () => new Map(
      (Individual.fromJSONArray(teamMemberIndividualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual.fullName,
      ]),
    ),
    [teamMemberIndividualsQuery.data],
  );
  /**
   * How one category's Team Member cell reads: the person's name, or the table's own word for the
   * states that have no name to give.
   *
   * Named rather than written inline because the column needs it twice, at two different moments —
   * see the note on the column itself.
   */
  const teamMemberText = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string => {
      if (category.teamMemberIsMasked) return proposalFeeScheduleRestrictedTeamMemberText;
      if (!category.teamMember) return proposalFeeScheduleUnassignedText;
      return (
        teamMemberNames.get(category.teamMember.userId.value) ?? proposalFeeScheduleTeamMemberFallbackText
      );
    },
    [teamMemberNames],
  );

  // #region Functions
  const confirmRemoveCategory = useCallback(
    async (
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      if (!category) return;

      const response = await confirm({
        title: "Delete Fee Schedule Category?",
        message: `Delete the ${category.name} fee schedule category?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      onCategoryRemoved?.(category.name);
    },
    [confirm, onCategoryRemoved],
  );

  const handleCategoryRevert = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency, entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a fee-schedule category), so
      // `entry.operation` is always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      // A single-column revert (#4847) carries that column alone, which is not a category (#7027). It is
      // merged onto the category as it stands, so the other columns keep their values, as on the active
      // proposal's schedule. A whole-entry revert still replaces the category.
      const revertedJSON = proposalCollectionItemRevertAspect(entry.operation)
        ? { ...category.toJSON(), ...(revertValue as object) }
        : revertValue;
      const revertedCategory = FeeScheduleCategory.fromJSON(revertedJSON as never);
      if (!revertedCategory) return;
      onCategoryReverted?.(revertedCategory);
    },
    [onCategoryReverted],
  );

  const beginEditCategory = useCallback(
    (
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      if (!category || disableEditing || isRemovedCategory(category)) return;
      onEditCategory?.(category);
    },
    [disableEditing, isRemovedCategory, onEditCategory],
  );

  /**
   * #5455: the schedule sorts and pages its own rows now — see the model this follows,
   * `readonly-fee-schedule-categories-table`. A filter or a sort that leaves fewer rows than the page
   * the reader was on left them looking at an empty page there; jumping back to the first page on
   * either change is what that table already does.
   */
  const resetToFirstPage = useCallback(() => {
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  }, []);

  const handleColumnFiltersChange = useCallback((updater: MRT_Updater<ColumnFiltersState>) => {
    resetToFirstPage();
    setColumnFilters(updater);
  }, [resetToFirstPage]);

  const handleGlobalFilterChange = useCallback((updater: MRT_Updater<string>) => {
    resetToFirstPage();
    setQuery(updater);
  }, [resetToFirstPage]);

  const handleSortingChange = useCallback((updater: MRT_Updater<SortingState>) => {
    resetToFirstPage();
    setSorting(updater);
  }, [resetToFirstPage]);
  // #endregion

  /**
   * The category's name, and the row's own statement that it opens something.
   *
   * A row here opens the inline editor when pressed, and used to say so with nothing but
   * `cursor: pointer` — the signal #5122 is about, the one you have to already be hovering to
   * receive. `RowLink` is the application's answer to that: underlined at rest, focusable, and a
   * button rather than an anchor because the editor opens in place and there is no URL to point at.
   *
   * A row that cannot be opened is handed no `onClick` at all, and `RowLink` then renders exactly
   * the text it rendered before — a locked schedule, a removed category and a load issue must not
   * claim to be editable.
   */
  const NameCell = useCallback(({ row, table }: {
    row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    if (table.getState().isLoading) return;
    if (isFeeScheduleTemplateLoadIssue(row.original)) {
      return (
        <Typography color="error">{`Error loading category: ${row.original.issue}`}</Typography>
      );
    }
    const value = row.original.name.value;
    if (!value) return <FeeScheduleTableCellNoValue />;
    const opensEditor = !disableCategorySelection && !disableEditing && !isRemovedCategory(row.original);
    return (
      <RowLink ariaLabel={value} onClick={opensEditor ? () => beginEditCategory(row.original) : undefined}>
        <FeeScheduleTableCellText emphasized title={value}>
          <SearchMatch table={table} text={value} />
        </FeeScheduleTableCellText>
      </RowLink>
    );
  }, [beginEditCategory, disableCategorySelection, disableEditing, isRemovedCategory]);

  const FeeCell = useCallback(({ row, table }: {
    row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
    let feeValue;
    if (row.original instanceof FeeScheduleCategoryMaskedCurrency) {
      feeValue = "Masked";
    } else if (row.original.fee == null) {
      const deferredText = `Deferred to ${session.context?.viewingAsVendor ? "client" : "vendor"}`;
      const issue = feeIssues?.get(row.original.id.value);
      feeValue = issue ? (
        <FeeScheduleIssueFlag
          reason={issue.description}
          severity={issueFlagSeverity(issue)}
          cellKey={feeScheduleIssueCellKey(row.original.id.value, ProposalIssuePart.Fee)}
        >
          <FeeScheduleTableCellText title={deferredText}>
            <SearchMatch table={table} text={deferredText} />
          </FeeScheduleTableCellText>
        </FeeScheduleIssueFlag>
      ) : (
        <FeeScheduleTableCellText color="warning" title={deferredText}>
          <SearchMatch table={table} text={deferredText} />
        </FeeScheduleTableCellText>
      );
    } else {
      feeValue = new Intl.NumberFormat(navigator.language, {
        style: "currency",
        currency: row.original.fee?.rate?.currency.toString() ?? "USD",
      }).format(row.original.fee?.rate?.amount ?? 0);
    }
    return typeof feeValue === "string" ? <SearchMatch table={table} text={feeValue} /> : feeValue;
  }, [feeIssues, session.context?.viewingAsVendor]);

  // #region Columns
  const columns = useMemo(
    () => [
      ...(disableEditing && fieldLockTooltip
        ? [
          columnHelper.display({
            header: "",
            id: "locked",
            meta: { kind: "actions" },
            ...startingWidth("locked"),
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                verticalAlign: "middle",
              },
            },
            Cell: () => (
              <LockedCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </LockedCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(getCategoryNameValue, {
        header: "Name",
        id: "name",
        meta: { kind: "name" },
        ...startingWidth("name"),
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: NameCell
      }),
      columnHelper.accessor(getCategoryDescriptionValue, {
        header: "Description",
        id: "description",
        // A description is capped rather than handed the slack (#5111).
        meta: { kind: "text", width: { maximum: feeScheduleColumnWidth("description").maximum } },
        ...startingWidth("description"),
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: DescriptionCell
      }),
      // Fee or Expense, read with the name and description it qualifies (#6882).
      columnHelper.accessor(getCategoryTypeValue, {
        header: "Type",
        id: "type",
        meta: { kind: "status" },
        ...startingWidth("type"),
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: TypeCell
      }),
      columnHelper.accessor(getCategoryFixedFeeValue, {
        header: "Fixed?",
        id: "fixedFee",
        meta: { kind: "status" },
        ...startingWidth("fixedFee"),
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: FixedFeeCell
      }),
      columnHelper.accessor(
        (category) => getCategoryFeeValue(category, session.context?.viewingAsVendor),
        {
          header: "Fee",
          id: "fee",
          meta: { kind: "money" },
          ...startingWidth("fee"),
          enableGlobalFilter: true,
          filterFn: "includesString",
          Cell: FeeCell
        }
      ),
      columnHelper.accessor(getCategoryUnitValue, {
        header: "Per",
        id: "unit",
        meta: { kind: "text" },
        ...startingWidth("unit"),
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: UnitCell
      }),
      ...(hideTeamMemberColumn
        ? []
        : [
            columnHelper.accessor(teamMemberText, {
              header: "Team Member",
              id: "teamMember",
              meta: { kind: "person" },
              ...startingWidth("teamMember"),
              enableGlobalFilter: true,
              filterFn: "includesString",
              /**
               * Read from the category, never from `cell.getValue()`.
               *
               * TanStack keeps a row's accessor values on the row object and rebuilds the row model
               * only when `data` changes identity — `getCoreRowModel` memoizes on `table.options.data`
               * and on nothing else, so a new column definition does not disturb a value already
               * cached there. The Individuals lookup that names the assignee answers after the first
               * render, and a schedule nobody has edited since hands the table the same categories,
               * so `cell.getValue()` returned the stand-in this column was first drawn with for as
               * long as the page stayed open: every staffed rate in the product read "Team member"
               * rather than a name (#5228). Resolving here instead means the reader sees the name as
               * soon as it exists. The accessor above remains what search and sorting read.
               */
              Cell: ({ row, table }) => {
                const name = teamMemberText(row.original);
                const text = (
                  <FeeScheduleTableCellText title={name}>
                    <SearchMatch table={table} text={name} />
                  </FeeScheduleTableCellText>
                );
                const issue = teamMemberIssues?.get(row.original.id.value);
                if (issue) {
                  return (
                    <FeeScheduleIssueFlag
                      reason={issue.description}
                      severity={issueFlagSeverity(issue)}
                      cellKey={feeScheduleIssueCellKey(row.original.id.value, ProposalIssuePart.TeamMember)}
                    >
                      {text}
                    </FeeScheduleIssueFlag>
                  );
                }
                const isOffTeam =
                  !row.original.teamMemberIsMasked && (teamRoster?.isOffTeam(row.original.teamMember) ?? false);
                return isOffTeam
                  ? <FeeScheduleIssueFlag reason={feeScheduleOffTeamFlagText}>{text}</FeeScheduleIssueFlag>
                  : text;
              },
            }),
          ]),
    ],
    [FeeCell, NameCell, disableEditing, fieldLockTooltip, hideTeamMemberColumn, session.context?.viewingAsVendor, teamMemberIssues, teamMemberText, teamRoster]
  );
  // #endregion

  // #region Table Option Functions
  const getRowId = useCallback(
    (row: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency, index: number) => `${row?.id?.value ?? "category"}-${index}`,
    [],
  );

  const muiTableProps = useCallback(() => ({
    sx: {
      // Stated rather than left out: the fit has no width to spare for spacing between the rows'
      // cells. Nothing needs the rows held apart any more — see `feeScheduleCategoryRowStyles`; this
      // used to be raised to 3px while a row was pulsing.
      borderSpacing: 0,
    },
  }), []);

  const muiTableBodyRowProps = useCallback(({ row }: {
    row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const hasPendingChange = fieldHasPendingChange(
      pendingChangeFields,
      ProposalField.FeeScheduleCategory(row.original.id),
    );
    const isActive = isActiveFeeScheduleCategoryField(activeReviewField, row.original.id);
    // Marks the row as the stop's highlight, so the issue above the table stays unlit and the page can
    // scroll to it — see `traverser-stop-scope`.
    const className = traverserStopClassName(isActive);
    return disableCategorySelection || disableEditing || isRemovedCategory(row.original)
      ? {
        className,
        sx: (theme: Theme) => ({
          ...feeScheduleCategoryRowStyles(theme, hasPendingChange, isActive),
          "& tr.MuiTableRow-root": {
            cursor: "default !important",
          },
          cursor: "default",
        }),
      }
      : {
        className,
        onClick: () => beginEditCategory(row.original),
        sx: (theme: Theme) => ({
          ...feeScheduleCategoryRowStyles(theme, hasPendingChange, isActive),
          cursor: "pointer",
        }),
      };
  }, [disableCategorySelection, disableEditing, isRemovedCategory, beginEditCategory, activeReviewField, pendingChangeFields]);

  const renderEmptyRowsFallback = useMemo(
    () =>
      emptyRowsFallback<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>({
        subject: "fee schedule categories",
      }),
    [],
  );

  /**
   * The ways this schedule can gain a category, as one split button rather than a row of up to three
   * equal ones (#4955). The first is the press the button itself makes; the rest are the alternatives
   * behind its dropdown. Which of them are offered is the caller's, exactly as before — a schedule that
   * hides "Add New Category" and keeps "From Existing Template" simply has that one as its press.
   */
  const addActions = useMemo(() => {
    if (disableEditing) return [];
    const waysToAdd = [
      ...(hideAddButton ? [] : [{ label: ADD_NEW_CATEGORY_LABEL, onClick: () => onCreateCategoryClicked?.() }]),
      ...(hideExistingButton
        ? []
        : [
          {
            label: ADD_EXISTING_CATEGORY_LABEL,
            icon: <SnippetFolderIcon fontSize="small" />,
            onClick: () => onExistingCategoryClicked?.(),
          },
        ]),
      ...(hideFromTemplateButton
        ? []
        : [
          {
            label: FROM_EXISTING_TEMPLATE_LABEL,
            icon: <FileCopyIcon fontSize="small" />,
            onClick: () => onExistingTemplateClicked?.(),
          },
        ]),
    ];
    return waysToAdd;
  }, [
    disableEditing,
    hideAddButton,
    hideExistingButton,
    hideFromTemplateButton,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onExistingTemplateClicked,
  ]);

  /**
   * The search first, the add control beside it, then the caller's own actions and the view buttons at
   * the far end — one wrapping row, so the add control sits to the RIGHT of the search wherever there
   * is room and drops underneath where there is not (#4955).
   *
   * One site rather than two. The toolbar used to be drawn at whichever of MRT's two slots suited the
   * measured pane width; the row wraps now, which answers the same question from the real width at
   * every width, so `toolbarIsNarrow` decides nothing here any more.
   */
  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        {!disableEditing && (
          <TableAddActions
            actions={addActions}
            standaloneActions={saveAsTemplateAction ? [saveAsTemplateAction] : undefined}
          />
        )}
        {!disableEditing && toolbarActions}
        {/* No whole-schedule history here: a category's history lives on its row, and a removed
            category keeps a row of its own (#4951). The narrow arrangement had kept a copy of it that
            the wide one had already lost, which is the kind of thing one toolbar rather than two puts
            a stop to. */}
        <CollectionToolbarViewButtons>
          <TableToolsMenu table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    [addActions, disableEditing, saveAsTemplateAction, toolbarActions],
  );

  // No whole-schedule history here: a category's history lives on its row, and a removed category keeps
  // a row of its own (#4951). Reading the schedule's history beside the categories' was the confusion.
  //
  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the page would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  const renderRowActions = useCallback((props: {
    row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const { row } = props;
    // A category the draft removed since its last save: Undo in place of Remove, and the category's
    // own history, read-only, as a removed row's always is (#6995).
    if (!disableEditing && draftChanges && draftRemovedCategoryIds.has(row.original.id.value)) {
      return (
        <RowActionsContainer>
          <FieldUndoButton
            onUndo={(event) => {
              event.stopPropagation();
              draftChanges.onCategoryUndone(row.original);
            }}
          />
          {showFieldHistory && (
            <ProposalFieldHistoryButton
              field={ProposalField.FeeScheduleCategory(row.original.id)}
              collectionItemId={row.original.id.value}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
            />
          )}
        </RowActionsContainer>
      );
    }
    // A row nobody may write to — the viewer is locked out, or the category is one the schedule no
    // longer has — carries the category's own history and nothing else, the read-only popover rather
    // than the revert menu (the same split the fee-schedule redline and the Details tab make). Scoped to
    // the category: the schedule as a whole has no history control of its own any more (#4951).
    if (disableEditing || isRemovedCategory(row.original)) {
      if (!showFieldHistory) return null;
      return (
        <RowActionsContainer>
          <ProposalFieldHistoryButton
            field={ProposalField.FeeScheduleCategory(row.original.id)}
            collectionItemId={row.original.id.value}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
          />
        </RowActionsContainer>
      );
    }
    // #5797: the same Edit action the active proposal's schedule carries, in the same place. The row
    // and its name link open the editor too; see "Fee schedule categories, everywhere" in
    // table-row-actions.md.
    const opensEditor = Boolean(onEditCategory)
      && !disableCategorySelection
      && !isFeeScheduleTemplateLoadIssue(row.original);
    const categoryName = row.original.name?.value;
    const history = hideFieldHistory ? null : (
      <ProposalFieldTimelineDropdown
        field={ProposalField.FeeScheduleCategory(row.original.id)}
        negotiationId={negotiationId}
        viewerScope={viewerScope}
        toolTip="Column change history"
        // One section per column, as on the active proposal's schedule (#4847, #7027).
        collectionItemAspects={proposalFeeScheduleAspectOptions}
        onRevert={(entry) => handleCategoryRevert(row.original, entry)}
      />
    );
    if (isPhone) {
      const actions: RowAction[] = [
        ...(opensEditor
          ? [{
            key: "edit",
            label: "Edit Category",
            ariaLabel: categoryName ? `Edit category: ${categoryName}` : "Edit category",
            icon: <EditIcon />,
            onClick: () => beginEditCategory(row.original),
          }]
          : []),
        // An edited category's Undo, between Edit and Remove as on a wider screen (#6995).
        ...(draftChanges?.editedCategoryIds.has(row.original.id.value)
          ? [{
            key: "undo",
            label: FIELD_UNDO_LABEL,
            icon: <UndoIcon />,
            disabled: draftChanges.undoBlockedReasons?.has(row.original.id.value),
            disabledReason: draftChanges.undoBlockedReasons?.get(row.original.id.value),
            onClick: () => draftChanges.onCategoryUndone(row.original),
          }]
          : []),
        {
          key: "remove",
          label: "Remove Category",
          icon: <DeleteOutlinedIcon />,
          destructive: true,
          onClick: () => void confirmRemoveCategory(row.original),
        },
      ];
      // Two or more actions are one menu; a lone Remove Category stays a button, since a menu of one is a
      // second press for nothing. The history stays beside them: it opens a popover of its own.
      return (
        <RowActions
          collapsed={actions.length > 1}
          ariaLabel={categoryName ? `Actions for ${categoryName}` : "Category actions"}
          actions={actions}
          trailing={history}
        />
      );
    }
    return (
      <RowActionsContainer>
        {opensEditor && (
          <Tooltip title="Edit Category">
            <span>
              <EditActionButton
                aria-label={categoryName ? `Edit category: ${categoryName}` : "Edit category"}
                onClick={(event) => {
                  // The row opens the editor too; without this one press would open it twice.
                  event.stopPropagation();
                  beginEditCategory(row.original);
                }}
              >
                <EditIcon />
              </EditActionButton>
            </span>
          </Tooltip>
        )}
        {draftChanges?.editedCategoryIds.has(row.original.id.value) && (
          <FieldUndoButton
            disabledReason={draftChanges.undoBlockedReasons?.get(row.original.id.value)}
            onUndo={(event) => {
              // The row opens the editor too.
              event.stopPropagation();
              draftChanges.onCategoryUndone(row.original);
            }}
          />
        )}
        <Tooltip title="Remove Category">
          <span>
            <ProposalDeleteActionButton
              aria-label="Remove Category"
              onClick={(event) => {
                event.stopPropagation();
                void confirmRemoveCategory(row.original);
              }}
            />
          </span>
        </Tooltip>
        {history}
      </RowActionsContainer>
    );
  }, [
    beginEditCategory,
    confirmRemoveCategory,
    disableCategorySelection,
    disableEditing,
    draftChanges,
    draftRemovedCategoryIds,
    handleCategoryRevert,
    hideFieldHistory,
    isPhone,
    isRemovedCategory,
    negotiationId,
    onEditCategory,
    showFieldHistory,
    viewerScope,
  ]);

  // #region Table Options
  /**
   * One answer for whether the actions column exists. A locked viewer still gets the column when there
   * is history to open from it (#4951).
   */
  const showActionsColumn = !hideActionsColumn && (!disableEditing || showFieldHistory);

  const columnVisibility = useMemo(
    () => ({
      // The row's Edit action, its name link and the row itself open the inline editor, so MRT's own
      // expander column would be a fourth control for the same thing.
      ...(renderCategoryEditor ? { "mrt-row-expand": false } : {}),
      ...chosenColumnVisibility,
    }),
    [chosenColumnVisibility, renderCategoryEditor],
  );

  /**
   * Which row, if any, is showing its editor — keyed the way `getRowId` keys rows rather than by the
   * category's own id, because that is the key MRT's expansion state is written in.
   */
  const expandedRows = useMemo(() => {
    if (!renderCategoryEditor || !editingCategoryId) return {};
    const index = rows.findIndex((row) => row?.id?.value === editingCategoryId);
    if (index < 0) return {};
    // Hoisted rather than written inline as `{ [getRowId(...)]: true }`: a call expression as a
    // computed key is one of the patterns the React Compiler cannot lower, and a bailout costs the
    // whole component its memoization silently. See app/CLAUDE.md -> React Compiler.
    const expandedRowId = getRowId(rows[index], index);
    return { [expandedRowId]: true };
  }, [editingCategoryId, getRowId, renderCategoryEditor, rows]);

  /** The row the traverser is on, so the table turns to its page when it lands (#6617). */
  const activeRowId = useMemo(() => {
    const index = rows.findIndex((row) => isActiveFeeScheduleCategoryField(activeReviewField, row.id));
    return index < 0 ? undefined : getRowId(rows[index], index);
  }, [activeReviewField, getRowId, rows]);

  const tableOptions = useMemo<
    MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  >(
    () => ({
      columns,
      data: rows,
      enableRowSelection: false,
      enableTableHead: true,
      getRowId,
      initialState: {
        showColumnFilters: false,
        showGlobalFilter: true,
        columnPinning: { right: ["mrt-row-actions"] },
      },
      enableColumnPinning: true,
      enableColumnFilters: true,
      enableGlobalFilter: true,
      enableGlobalFilterRankedResults: false,
      globalFilterFn: "includesString",
      manualFiltering: false,
      enableStickyHeader: true,
      enableStickyFooter: true,
      // #5455: these were `true`, which tells MRT the rows arrive already paged and sorted and to
      // leave them alone — while nothing here ever paged or ordered `rows` itself. The pager and every
      // `⋮ → Sort by …` item changed nothing. The rows are a handful of categories held in memory, so
      // MRT pages and sorts them here, the way `readonly-fee-schedule-categories-table` already does.
      manualPagination: false,
      // TanStack Table turns back to page 1 whenever the data is new, and the page hands this table new
      // data on every save and every change that arrives. A row the traverser had just turned to page 2
      // for went back to page 1 a moment later (#6617). A search, a filter or a sort still returns to
      // page 1 through its own handler, and Material React Table pulls a page that has emptied back in range.
      autoResetPageIndex: false,
      manualSorting: false,
      // A locked viewer still gets the actions column when there is history to open from it: without
      // the whole-schedule control, a category row is the only place its history can be reached.
      enableRowActions: showActionsColumn,
      onColumnFiltersChange: handleColumnFiltersChange,
      onGlobalFilterChange: handleGlobalFilterChange,
      onSortingChange: handleSortingChange,
      onPaginationChange: setPagination,
      onColumnVisibilityChange: setChosenColumnVisibility,
      ...(renderCategoryEditor
        ? {
          // Only the open row builds an editor. MRT asks every row for its panel, and a form per
          // row would mount a fee category editor for each category in the schedule.
          renderDetailPanel: ({ row }: {
            row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
          }) => (expandedRows[row.id] ? renderCategoryEditor(row.original) : null),
          muiDetailPanelProps: { sx: { padding: 0 } },
          enableExpandAll: false,
        }
        : {}),
      state: {
        columnFilters,
        columnVisibility,
        globalFilter: query,
        pagination,
        sorting,
        ...(renderCategoryEditor ? { expanded: expandedRows } : {}),
      },
      muiTableProps,
      // The actions column centres its controls; its width is measured from them like every other
      // column's, since this is a list table (#6708).
      displayColumnDefOptions: {
        "mrt-row-actions": {
          muiTableHeadCellProps: { align: "center" as const, sx: { px: 0.5 } },
          muiTableBodyCellProps: {
            align: "center" as const,
            sx: { px: 0.5, "& > *": { justifyContent: "center" } },
          },
        },
      },
      muiTableBodyRowProps,
      muiTableBodyCellProps: ({ cell, row }) => ({
        sx: (cellTheme) => ({
          ...feeScheduleTableCellStyles(cellTheme, { columnId: cell.column.id }),
          // A removed category reads as a deletion, the way a removed row of a redline does.
          ...(isRemovedCategory(row.original) && cell.column.id !== "mrt-row-actions" && removedTableRowCellStyles(cellTheme)),
        }),
      }),
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions,
      positionGlobalFilter: "none",
      muiTableContainerProps: {
        sx: {
          flex: 1,
          minWidth: 0,
          position: "relative",
        },
      },
      muiTopToolbarProps: {
        sx: {
          // A zero floor on the inner Box is what lets MRT's toolbar grid track clamp to the pane
          // rather than size itself to its content — without it the toolbar, which is
          // `overflow: hidden`, clips whatever runs past the right edge and the one wrapping row
          // inside it never gets the chance to wrap.
          "& > .MuiBox-root": {
            alignItems: "center",
            flexDirection: "row",
            // MRT's own 8px padding is the whole inset, as on every other table; another 8px here put
            // the search 8px inside the edge of the proposal tab's issue notices above it.
            minWidth: 0,
            ...(tableIsCompact && {
              flexDirection: "column",
              gap: 0,
              padding: 0,
            }),
          },
        },
      },
      muiTablePaperProps: {
        ...paneStretchTable,
        elevation: 0,
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
          "& tr:only-of-type td[colspan]": {
            height: "100%",
            verticalAlign: "middle",
            padding: 0,
            borderBottom: "none",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: "Search",
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setQuery("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
    }),
    [
      columns,
      rows,
      getRowId,
      isRemovedCategory,
      showActionsColumn,
      columnFilters,
      query,
      sorting,
      pagination,
      columnVisibility,
      setChosenColumnVisibility,
      tableIsCompact,
      muiTableProps,
      muiTableBodyRowProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions,
      renderCategoryEditor,
      expandedRows,
      handleColumnFiltersChange,
      handleGlobalFilterChange,
      handleSortingChange,
    ],
  );
  // #endregion

  // #region Render
  return (
    <TableContainer className={className}>
      {alertBannerMessage && (
        <Alert
          severity={alertBannerSeverity}
          sx={{
            mb: 2,
            width: "100%",
            whiteSpace: "normal",
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          {alertBannerMessage}
        </Alert>
      )}
      <MRTNoMemo<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
        options={tableOptions}
        columnChoice={chosenColumnVisibility}
        revealRowId={activeRowId}
        onDrawnRowsChange={(drawn) => onDisplayedCategoriesChange?.(drawn?.map((category) => category.id))}
        scrollButtons
      />
    </TableContainer>
  );
  // #endregion
}

function getCategoryNameValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return `Error loading category: ${category.issue}`;
  return category.name?.value ?? "";
}

function getCategoryDescriptionValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  return category.description?.value ?? "";
}

function getCategoryFixedFeeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category?.fee?.unit ? "No" : "Yes";
}

function getCategoryFeeValue(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  viewingAsVendor?: boolean,
): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  if (!category.fee) return `Deferred to ${viewingAsVendor ? "client" : "vendor"}`;

  return new Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency: category.fee?.rate?.currency.toString() ?? "USD",
  }).format(category.fee?.rate?.amount ?? 0);
}

function getCategoryUnitValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category.fee?.unit ?? "";
}

function getCategoryTypeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  // MRT hands its loading placeholder rows to accessors too, and they are not categories.
  if (!(category instanceof FeeScheduleCategory) && !(category instanceof FeeScheduleCategoryMaskedCurrency)) return "";
  return feeScheduleCategoryTypeLabel(category.type);
}

export function isActiveFeeScheduleCategoryField(
  activeReviewField: ProposalField | undefined,
  categoryId: Guid,
): boolean {
  return activeReviewField?.isEqualTo(ProposalField.FeeScheduleCategory(categoryId)) ?? false;
}

// #region Cells
// The table searches every column in hand, so each cell marks what the search box holds (#6590).
const DescriptionCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  const value = row.original.description?.value;
  if (!value) return <FeeScheduleTableCellNoValue />;
  return (
    <FeeScheduleTableCellText title={value}>
      <SearchMatch table={table} text={value} />
    </FeeScheduleTableCellText>
  );
};

/** A flagged cell takes its issue's level: a blocker is an error, anything else a warning. */
function issueFlagSeverity(issue: ProposalIssue): FeeScheduleIssueFlagSeverity {
  return issue.level === ProposalIssueLevel.Critical ? "error" : "warning";
}

const FixedFeeCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  let fixedValue;
  if (row.original instanceof FeeScheduleCategoryMaskedCurrency) {
    fixedValue = "Masked";
  } else if (row.original?.fee?.unit) {
    fixedValue = "No";
  } else {
    fixedValue = "Yes";
  }
  return <SearchMatch table={table} text={fixedValue} />;
};

const UnitCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  const unit = row.original instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : row.original.fee?.unit;
  if (unit === undefined || unit === null) return <FeeScheduleTableCellNoValue />;
  return <SearchMatch table={table} text={unit} />;
};

const TypeCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  // A masked currency hides the rate, not whether the category is a fee or an expense.
  return <SearchMatch table={table} text={getCategoryTypeValue(row.original)} />;
};
// #endregion
